/**
 * Vanilla port of React Bits Infinite Spiral
 * https://reactbits.dev/components/infinite-spiral
 * Source: https://github.com/DavidHDev/react-bits
 */
(function (global) {
  'use strict';

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
  function modulo(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
  }
  function smoothstep(min, max, value) {
    var x = clamp((value - min) / (max - min || 1), 0, 1);
    return x * x * (3 - 2 * x);
  }

  function normalizeItems(items) {
    return (items || []).map(function (item, index) {
      if (typeof item === 'string') {
        return { src: item, alt: 'Spiral image ' + (index + 1) };
      }
      return Object.assign({ alt: 'Spiral image ' + (index + 1) }, item);
    }).filter(function (item) { return item && item.src; });
  }

  function InfiniteSpiral(root, options) {
    if (!root) return null;
    options = options || {};

    var baseItems = normalizeItems(options.items);
    if (!baseItems.length) return null;

    /* Keep enough cards so the ribbon stays dense like the React Bits demo */
    var items = baseItems.slice();
    while (items.length < 12) {
      items = items.concat(baseItems.map(function (item, i) {
        return Object.assign({}, item, { alt: (item.alt || 'Image') + ' copy ' + (i + 1) });
      }));
    }
    if (items.length > 20) items = items.slice(0, 20);

    var speed = options.speed != null ? options.speed : 0.55;
    var direction = options.direction || 'up';
    var animationMode = options.animationMode || 'auto';
    var radius = options.radius != null ? options.radius : 170;
    var cardWidth = options.cardWidth != null ? options.cardWidth : 100;
    var cardHeight = options.cardHeight != null ? options.cardHeight : 100;
    var verticalSpacing = options.verticalSpacing != null ? options.verticalSpacing : 60;
    var perspective = options.perspective != null ? options.perspective : 1000;
    var cardsPerTurn = options.cardsPerTurn != null ? options.cardsPerTurn : 7;
    var rotation = options.rotation != null ? options.rotation : 0;
    var cardTilt = options.cardTilt != null ? options.cardTilt : 0;
    var cardRadius = options.cardRadius != null ? options.cardRadius : 10;
    var centerScale = options.centerScale != null ? options.centerScale : 1.2;
    var edgeFade = options.edgeFade != null ? options.edgeFade : 0.3;
    var edgeBlur = options.edgeBlur != null ? options.edgeBlur : 6;
    var pauseOnHover = options.pauseOnHover !== false;
    var imageFit = options.imageFit || 'cover';
    var grayscale = options.grayscale != null ? options.grayscale : 0;

    var dragEnabled =
      animationMode === 'drag' ||
      animationMode === 'all' ||
      animationMode === 'auto-drag';
    var scrollEnabled = animationMode === 'scroll' || animationMode === 'all';
    var autoEnabled =
      animationMode === 'auto' ||
      animationMode === 'all' ||
      animationMode === 'auto-drag';

    root.classList.add('infinite-spiral');
    root.style.perspective = perspective + 'px';
    root.style.setProperty('--infinite-spiral-card-width', cardWidth + 'px');
    root.style.setProperty('--infinite-spiral-card-height', cardHeight + 'px');
    root.style.setProperty('--infinite-spiral-card-radius', cardRadius + 'px');
    root.style.cursor = dragEnabled ? 'grab' : 'default';
    root.style.touchAction = dragEnabled ? 'pan-x' : 'auto';
    root.style.userSelect = dragEnabled ? 'none' : 'auto';
    root.setAttribute('role', 'list');
    root.setAttribute('aria-label', options.ariaLabel || 'Photo spiral');

    var stage = document.createElement('div');
    stage.className = 'infinite-spiral__stage';
    root.appendChild(stage);

    var cardEls = items.map(function (item, index) {
      var card = document.createElement(item.href ? 'a' : 'div');
      card.className = 'infinite-spiral__item';
      card.setAttribute('role', 'listitem');
      card.setAttribute('aria-label', item.label || item.alt);
      card.style.width = cardWidth + 'px';
      card.style.height = cardHeight + 'px';
      card.style.borderRadius = cardRadius + 'px';
      if (item.href) {
        card.href = item.href;
        if (item.target) card.target = item.target;
        if (item.target === '_blank') card.rel = 'noreferrer';
      }
      var img = document.createElement('img');
      img.className = 'infinite-spiral__image';
      img.src = item.src;
      img.alt = item.alt || '';
      img.draggable = false;
      img.loading = index < 6 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.style.objectFit = imageFit;
      if (grayscale > 0) {
        img.style.filter = 'grayscale(' + Math.min(1, Math.max(0, grayscale)) + ')';
      }
      img.addEventListener('error', function () {
        card.style.display = 'none';
        card.setAttribute('aria-hidden', 'true');
      });
      card.appendChild(img);
      stage.appendChild(card);
      return card;
    });

    var progress = 0;
    var targetProgress = 0;
    var autoSpeed = 0;
    var hovered = false;
    var visible = true;
    var dragging = false;
    var lastPointerY = 0;
    var dragMoved = false;
    var frameId = 0;
    var previousTime = performance.now();
    var bounds = root.getBoundingClientRect();
    var lastScrollY = window.scrollY;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var scrollSpeedMultiplier = Math.max(speed, 0) / 0.55;

    var resizeObserver = new ResizeObserver(function () {
      bounds = root.getBoundingClientRect();
    });
    resizeObserver.observe(root);

    var intersectionObserver = new IntersectionObserver(function (entries) {
      if (entries[0]) visible = entries[0].isIntersecting;
    }, { threshold: 0.02 });
    intersectionObserver.observe(root);

    function handleScroll() {
      var nextScrollY = window.scrollY;
      var scrollDelta = nextScrollY - lastScrollY;
      lastScrollY = nextScrollY;
      if (!scrollEnabled || !visible || scrollDelta === 0) return;
      targetProgress += clamp(
        (scrollDelta * scrollSpeedMultiplier) / Math.max(verticalSpacing * 2, 1),
        -1.5,
        1.5
      );
    }
    window.addEventListener('scroll', handleScroll, { passive: true });

    function render(time) {
      var delta = Math.min((time - previousTime) / 1000, 0.05);
      previousTime = time;

      var motionPaused = dragging || (pauseOnHover && hovered);
      var directionMultiplier = direction === 'down' ? -1 : 1;
      var desiredAutoSpeed =
        autoEnabled && visible && !reducedMotion.matches && !motionPaused
          ? speed * directionMultiplier
          : 0;
      var speedBlend = 1 - Math.exp(-delta * 4.5);
      autoSpeed += (desiredAutoSpeed - autoSpeed) * speedBlend;
      targetProgress += autoSpeed * delta;

      var followBlend = 1 - Math.exp(-delta * (dragging ? 18 : 6.5));
      progress += (targetProgress - progress) * followBlend;

      var count = items.length;
      var half = count / 2;
      var width = Math.max(bounds.width, 1);
      var height = Math.max(bounds.height, 1);
      var fit = Math.min(1, width / (cardWidth * 2.8), height / (cardHeight * 2.35));
      var responsiveRadius = Math.min(radius, Math.max(72, width * 0.36)) * fit;
      var fadeStart = clamp(1 - edgeFade, 0, 0.98);
      var turnSize = Math.max(cardsPerTurn, 1);

      /*
       * Exact React Bits placement:
       * https://github.com/DavidHDev/react-bits/.../InfiniteSpiral.jsx
       * Front of helix (high cos / depth) paints above — as a card moves
       * toward center it rises over the previous card.
       */
      cardEls.forEach(function (card, index) {
        if (!card) return;

        var offset = index - progress;
        offset = modulo(offset + half, count) - half;

        var edge = Math.min(Math.abs(offset) / Math.max(half, 1), 1);
        var opacity = 1 - smoothstep(fadeStart, 1, edge);
        var focus = 1 - Math.min(Math.abs(offset) / Math.max(turnSize * 0.65, 1), 1);
        var scale = (1 + (centerScale - 1) * focus) * fit;
        var angle = offset * (360 / turnSize) + rotation;
        var angleRadians = (angle * Math.PI) / 180;
        var x = Math.sin(angleRadians) * responsiveRadius;
        var z = Math.cos(angleRadians) * responsiveRadius;
        var depthScale = clamp(perspective / Math.max(perspective - z, 1), 0.72, 1.45);
        var visualScale = scale * depthScale;
        var depth = (z / Math.max(responsiveRadius, 1) + 1) / 2;
        var blur = edgeBlur * smoothstep(0.35, 1, edge);

        card.style.transform =
          'translate(-50%, -50%) translate3d(' +
          x + 'px, ' +
          offset * verticalSpacing * fit + 'px, 0) rotateZ(' +
          cardTilt + 'deg) scale(' +
          visualScale + ')';
        card.style.opacity = opacity.toFixed(3);
        card.style.filter = blur > 0.01 ? 'blur(' + blur.toFixed(2) + 'px)' : 'none';
        /* Depth only — never add array index (that put back cards on top) */
        card.style.zIndex = String(Math.round(depth * 100000));
        card.style.pointerEvents = opacity > 0.25 ? 'auto' : 'none';
      });

      frameId = requestAnimationFrame(render);
    }
    frameId = requestAnimationFrame(render);

    root.addEventListener('mouseenter', function () { hovered = true; });
    root.addEventListener('mouseleave', function () { hovered = false; });

    function stopDragging(event) {
      if (!dragging) return;
      dragging = false;
      if (event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      root.style.cursor = dragEnabled ? 'grab' : 'default';
    }

    if (dragEnabled) {
      root.addEventListener('pointerdown', function (event) {
        if (event.button !== 0) return;
        dragging = true;
        dragMoved = false;
        lastPointerY = event.clientY;
        targetProgress = progress;
        root.setPointerCapture(event.pointerId);
        root.style.cursor = 'grabbing';
      });
      root.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var pointerDelta = event.clientY - lastPointerY;
        lastPointerY = event.clientY;
        if (Math.abs(pointerDelta) > 0.5) dragMoved = true;
        targetProgress -= pointerDelta / Math.max(verticalSpacing, 1);
      });
      root.addEventListener('pointerup', stopDragging);
      root.addEventListener('pointercancel', stopDragging);
      root.addEventListener('click', function (event) {
        if (!dragMoved) return;
        event.preventDefault();
        event.stopPropagation();
        dragMoved = false;
      }, true);
    }

    return {
      destroy: function () {
        cancelAnimationFrame(frameId);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        window.removeEventListener('scroll', handleScroll);
        root.innerHTML = '';
      }
    };
  }

  global.SFInfiniteSpiral = InfiniteSpiral;
})(window);
